import * as React from 'react';
import { Chip, Box } from '@mui/material';
import { withTranslation } from 'react-i18next';

const creditNoteConfig = {
  color: '#7c2d12',
  bgcolor: '#ffedd5',
  borderColor: '#fed7aa',
};

// Settlement (reskontra) state, shown alongside the accounting status. "Hyvitetty" means the
// document no longer represents money owed — it does NOT mean it left the sales/VAT reports.
const settlementConfig = {
  credited: {
    color: '#3730a3',
    bgcolor: '#e0e7ff',
    borderColor: '#a5b4fc',
    labelKey: 'invoices.settlementCredited',
  },
  refund_pending: {
    color: '#9a3412',
    bgcolor: '#ffedd5',
    borderColor: '#fdba74',
    labelKey: 'invoices.settlementRefundPending',
  },
  refunded: {
    color: '#065f46',
    bgcolor: '#d1fae5',
    borderColor: '#6ee7b7',
    labelKey: 'invoices.settlementRefunded',
  },
};

// A draft that already holds money from a captured tender leg. It sits next to the
// amber "Draft" chip rather than replacing it, because the invoice really is still a
// draft — it just is not untouched any more, and an abandoned half-paid sale must not
// look like an ordinary unpaid one in the list.
const partiallyPaidConfig = {
  color: '#854d0e',
  bgcolor: '#fef9c3',
  borderColor: '#fde047',
};

const statusConfig = {
  draft: {
    color: '#92400e',
    bgcolor: '#fef3c7',
    borderColor: '#fcd34d',
  },
  sent: {
    color: '#1e40af',
    bgcolor: '#dbeafe',
    borderColor: '#93c5fd',
  },
  paid: {
    color: '#065f46',
    bgcolor: '#d1fae5',
    borderColor: '#6ee7b7',
  },
  overdue: {
    color: '#991b1b',
    bgcolor: '#fee2e2',
    borderColor: '#fca5a5',
  },
  cancelled: {
    color: '#374151',
    bgcolor: '#f3f4f6',
    borderColor: '#d1d5db',
  },
  combined: {
    color: '#374151',
    bgcolor: '#f3f4f6',
    borderColor: '#d1d5db',
  },
  refunded: {
    color: '#9333ea',
    bgcolor: '#f3e8ff',
    borderColor: '#d8b4fe',
  },
};

const InvoiceStatusBadge = ({ status, invoice_type, settlement_status, partially_paid, t, size = 'small' }) => {
  const config = statusConfig[status] || statusConfig.draft;
  const isCreditNote = invoice_type === 'INV02';
  const settlement = settlementConfig[settlement_status];

  const getLabel = () => {
    switch (status) {
      case 'draft':
        return t('Draft');
      case 'sent':
        return t('Sent');
      case 'paid':
        return t('Paid');
      case 'overdue':
        return t('Overdue');
      case 'cancelled':
        return t('Cancelled');
      case 'combined':
        return t('Combined');
      case 'refunded':
        return t('Refunded');
      default:
        return status;
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
      {isCreditNote && (
        <Chip
          label={t('invoices.creditNote')}
          size={size}
          sx={{
            color: creditNoteConfig.color,
            bgcolor: creditNoteConfig.bgcolor,
            border: `1px solid ${creditNoteConfig.borderColor}`,
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '12px',
            height: size === 'small' ? '22px' : '26px',
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      )}
      <Chip
        label={getLabel()}
        size={size}
        sx={{
          color: config.color,
          bgcolor: config.bgcolor,
          border: `1px solid ${config.borderColor}`,
          fontWeight: 500,
          fontSize: size === 'small' ? '11px' : '12px',
          height: size === 'small' ? '22px' : '26px',
          '& .MuiChip-label': {
            px: 1,
          },
        }}
      />
      {partially_paid && (
        <Chip
          label={t('invoices.partiallyPaid')}
          size={size}
          sx={{
            color: partiallyPaidConfig.color,
            bgcolor: partiallyPaidConfig.bgcolor,
            border: `1px solid ${partiallyPaidConfig.borderColor}`,
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '12px',
            height: size === 'small' ? '22px' : '26px',
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      )}
      {settlement && (
        <Chip
          label={t(settlement.labelKey)}
          size={size}
          sx={{
            color: settlement.color,
            bgcolor: settlement.bgcolor,
            border: `1px solid ${settlement.borderColor}`,
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '12px',
            height: size === 'small' ? '22px' : '26px',
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      )}
    </Box>
  );
};

export default withTranslation()(InvoiceStatusBadge);
